import { defineQuery, useQuery } from '@pinia/colada'
import { computed, type Ref } from 'vue'
import {
  type RentProjections,
  type CancelablePromise,
  type OccupancyProjections,
  type HistoricalOccupancy,
  type MarketFilters,
  marketReportSamples
} from 'common'
import { storeToRefs } from 'pinia'
import { useMarketAnalyticsQueryStore } from '@/stores/marketAnalyticsQueries.ts'
import { useStripeStore } from '@/stores/stripe.ts'
import { coreClient } from '@/api/coreclient.ts'
import { makeClosedSquareRing } from '@/utils'

function isSampleMarketCoordinates(coordinates: unknown): boolean {
  return JSON.stringify(marketReportSamples[0].filters.coordinates) === JSON.stringify(coordinates)
}

export const useRentProjections = defineQuery(() => {
  const { filters, filterKey, isQueryEnabled } = storeToRefs(useMarketAnalyticsQueryStore())
  let currentRequest: CancelablePromise<any> | null = null

  const { state, ...rest } = useQuery({
    key: () => ['rent_projections', filterKey.value as string],
    query: async (): Promise<{
      rentProjections: RentProjections
      occupancyProjections: OccupancyProjections
      historicalOccupancy: HistoricalOccupancy[]
    }> => {
      if (currentRequest) {
        currentRequest.cancel()
      }
      const client = await coreClient()
      const request = client.marketAnalytics.rentProjections(filters.value as MarketFilters)
      currentRequest = request
      const { data } = await request
      currentRequest = null
      return data
    },
    enabled: () =>
      Boolean(filterKey.value) &&
      (filters.value?.coordinates?.length ?? 0) > 0 &&
      isQueryEnabled.value &&
      (useStripeStore().hasRentProjections || isSampleMarketCoordinates(filters.value?.coordinates))
  })

  const rentProjections = computed(() =>
    rest.isLoading.value ? undefined : state.value?.data?.rentProjections
  )
  const occupancyProjections = computed(() =>
    rest.isLoading.value ? undefined : state.value?.data?.occupancyProjections
  )

  const historicalOccupancy = computed(() =>
    rest.isLoading.value ? undefined : state.value?.data?.historicalOccupancy
  )

  return {
    ...rest,
    rentProjections,
    occupancyProjections,
    historicalOccupancy
  }
})

export const useSinglePropertyRentProjection = (
  lat: Ref<number | null>,
  lng: Ref<number | null>,
  isLeaseUp?: Ref<boolean>
) => {
  let currentRequest: CancelablePromise<any> | null = null

  const { state, ...rest } = useQuery({
    key: () => [
      'single_property_rent_projections',
      lat.value,
      lng.value,
      isLeaseUp?.value ?? false
    ],
    query: async (): Promise<{
      rentProjections: RentProjections
      occupancyProjections: OccupancyProjections
      historicalOccupancy: HistoricalOccupancy[]
    }> => {
      if (currentRequest) {
        currentRequest.cancel()
      }
      const oneMileInMeters = 1609

      const polygon = makeClosedSquareRing(
        lat.value!,
        lng.value!,
        isLeaseUp?.value ? oneMileInMeters : 5
      )
      const today = new Date().toISOString().split('T')[0] as string
      const filters: MarketFilters = {
        coordinates: [[polygon]],
        date_range: { from: today, to: today }
      }

      try {
        const client = await coreClient()
        const request = client.marketAnalytics.rentProjections(filters)
        currentRequest = request
        const { data } = await request
        currentRequest = null
        return data
      } catch (e) {
        console.error('Error getting single property rent projection:', e)
        throw e
      }
    },
    enabled: () => lat.value !== null && lng.value !== null
  })

  const rentProjections = computed(() =>
    rest.isLoading.value ? undefined : state.value?.data?.rentProjections
  )
  const occupancyProjections = computed(() =>
    rest.isLoading.value ? undefined : state.value?.data?.occupancyProjections
  )

  const historicalOccupancy = computed(() =>
    rest.isLoading.value ? undefined : state.value?.data?.historicalOccupancy
  )

  return {
    ...rest,
    rentProjections,
    occupancyProjections,
    historicalOccupancy
  }
}
